import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Trash2, Save, X, Users, Luggage, Zap, Pencil, CarFront, Upload } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = { name: '', category: '', passengers: 3, luggage: 2, description: '', image: '', isElectric: false };

const FleetManagement = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Image too large (max 8MB)'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/fleet/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((prev) => ({ ...prev, image: res.data.url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const fetchFleet = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/fleet`);
      setVehicles(res.data.vehicles || []);
    } catch (e) {
      toast.error('Failed to load fleet');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFleet(); }, [fetchFleet]);


  const openAdd = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };
  const openEdit = (v) => {
    setForm({
      name: v.name || '', category: v.category || '', passengers: v.passengers ?? 3,
      luggage: v.luggage ?? 2, description: v.description || '', image: v.image || '', isElectric: !!v.isElectric,
    });
    setEditingId(v.id);
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Vehicle name is required'); return; }
    const payload = {
      ...form,
      passengers: parseInt(form.passengers, 10) || 0,
      luggage: parseInt(form.luggage, 10) || 0,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/admin/fleet/${editingId}`, payload);
        toast.success('Vehicle updated');
      } else {
        await axios.post(`${API}/admin/fleet`, payload);
        toast.success('Vehicle added');
      }
      closeForm();
      fetchFleet();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save vehicle');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this vehicle from the fleet?')) return;
    try {
      await axios.delete(`${API}/admin/fleet/${id}`);
      toast.success('Vehicle removed');
      fetchFleet();
    } catch (e) {
      toast.error('Failed to remove vehicle');
    }
  };

  return (
    <AdminLayout
      title="Fleet Management"
      subtitle="Add, edit or remove vehicles — used across the booking form, dispatch and public Fleet page"
      actions={!showForm && (
        <Button onClick={openAdd} className="bg-black text-white hover:bg-gray-800" data-testid="add-vehicle-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Vehicle
        </Button>
      )}
    >
      {showForm && (
        <Card className="p-6 mb-8" data-testid="fleet-form">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-black">{editingId ? 'Edit Vehicle' : 'Add Vehicle'}</h2>
            <Button onClick={closeForm} variant="ghost" size="sm"><X className="h-5 w-5" /></Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label className="text-gray-900 mb-2 font-medium">Vehicle Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="BMW i7 Luxury" className="bg-white border-gray-300" data-testid="fleet-name-input" />
            </div>
            <div>
              <Label className="text-gray-900 mb-2 font-medium">Category</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Luxury Saloon" className="bg-white border-gray-300" data-testid="fleet-category-input" />
            </div>
            <div>
              <Label className="text-gray-900 mb-2 font-medium">Passengers (PAX)</Label>
              <Input type="number" min="1" value={form.passengers} onChange={(e) => setForm({ ...form, passengers: e.target.value })} className="bg-white border-gray-300" data-testid="fleet-pax-input" />
            </div>
            <div>
              <Label className="text-gray-900 mb-2 font-medium">Luggage</Label>
              <Input type="number" min="0" value={form.luggage} onChange={(e) => setForm({ ...form, luggage: e.target.value })} className="bg-white border-gray-300" data-testid="fleet-luggage-input" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-gray-900 mb-2 font-medium">Vehicle Image</Label>
              <div className="flex items-start gap-4">
                <div className="h-24 w-36 rounded border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center flex-shrink-0">
                  {form.image ? (
                    <img src={form.image} alt="preview" className="w-full h-full object-cover" data-testid="fleet-image-preview" />
                  ) : (
                    <CarFront className="h-8 w-8 text-gray-300" />
                  )}
                </div>
                <div className="flex-1">
                  <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleImageUpload} data-testid="fleet-image-file" />
                  <Button type="button" variant="outline" className="border-gray-300" disabled={uploading} onClick={() => fileInputRef.current?.click()} data-testid="fleet-upload-btn">
                    <Upload className="h-4 w-4 mr-2" /> {uploading ? 'Uploading…' : 'Upload Photo'}
                  </Button>
                  <p className="text-xs text-gray-500 mt-2">PNG, JPG, WEBP or GIF · max 8MB. Or paste a URL below.</p>
                  <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://..." className="bg-white border-gray-300 mt-2" data-testid="fleet-image-input" />
                </div>
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-gray-900 mb-2 font-medium">Description (optional)</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description shown on the public Fleet page" className="bg-white border-gray-300" data-testid="fleet-description-input" />
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <Checkbox id="fleet-electric" checked={form.isElectric} onCheckedChange={(v) => setForm({ ...form, isElectric: !!v })} data-testid="fleet-electric-checkbox" />
              <Label htmlFor="fleet-electric" className="text-gray-900 font-medium flex items-center gap-1 cursor-pointer">
                <Zap className="h-4 w-4 text-emerald-600" /> Electric vehicle (shows an electric badge)
              </Label>
            </div>
          </div>
          <div className="flex gap-3">
            <Button onClick={closeForm} variant="outline" className="flex-1 border-gray-300">Cancel</Button>
            <Button onClick={handleSave} className="flex-1 bg-black text-white hover:bg-gray-800" data-testid="fleet-save-btn">
              <Save className="h-4 w-4 mr-2" /> {editingId ? 'Save Changes' : 'Add Vehicle'}
            </Button>
          </div>
        </Card>
      )}

      <h2 className="text-2xl font-bold mb-6 text-black">Fleet ({vehicles.length})</h2>
      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto" /></div>
      ) : vehicles.length === 0 ? (
        <Card className="p-12 text-center">
          <CarFront className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">No vehicles yet</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vehicles.map((v) => (
            <Card key={v.id} className="overflow-hidden" data-testid={`fleet-card-${v.id}`}>
              <div className="aspect-[16/10] bg-gray-100 overflow-hidden">
                {v.image ? (
                  <img src={v.image} alt={v.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300"><CarFront className="h-14 w-14" /></div>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-bold text-lg text-black flex items-center gap-1.5">
                      {v.name}
                      {v.isElectric && <Zap className="h-4 w-4 text-emerald-600" title="Electric" />}
                    </h3>
                    {v.category && <p className="text-xs uppercase tracking-wide text-gray-500 mt-0.5">{v.category}</p>}
                  </div>
                </div>
                {v.description && <p className="text-sm text-gray-600 mt-3 line-clamp-2">{v.description}</p>}
                <div className="flex items-center gap-4 mt-4 text-sm text-gray-700">
                  <span className="flex items-center gap-1.5"><Users className="h-4 w-4" /> {v.passengers}</span>
                  <span className="flex items-center gap-1.5"><Luggage className="h-4 w-4" /> {v.luggage}</span>
                </div>
                <div className="flex gap-2 mt-5 pt-4 border-t border-gray-200">
                  <Button onClick={() => openEdit(v)} variant="outline" className="flex-1 border-gray-300 h-9 text-xs" data-testid={`fleet-edit-${v.id}`}>
                    <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                  </Button>
                  <Button onClick={() => handleDelete(v.id)} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 h-9 text-xs" data-testid={`fleet-delete-${v.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default FleetManagement;
